import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  ChevronRight, 
  ChevronLeft,
  RotateCcw,
  Check,
  X,
  Sparkles
} from "lucide-react";

const FlashcardsPage = () => {
  const navigate = useNavigate();
  const [flashcards, setFlashcards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewedCount, setReviewedCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subjectsData, flashcardsData] = await Promise.all([
          api.get("/subjects"),
          api.get("/flashcards?limit=200")
        ]);
        setSubjects(subjectsData.subjects);
        setFlashcards(flashcardsData.flashcards);
      } catch (error) {
        console.error("Failed to fetch flashcards:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredFlashcards = selectedSubject 
    ? flashcards.filter(f => f.subject_id === selectedSubject)
    : flashcards;

  const currentCard = filteredFlashcards[currentIndex];

  const handleFlip = () => {
    setFlipped(!flipped);
  };

  const handleReview = async (quality) => {
    if (!currentCard) return;

    try {
      await api.post("/flashcards/review", {
        flashcard_id: currentCard.id,
        quality: quality
      });

      setReviewedCount(reviewedCount + 1);

      if (quality >= 3) {
        toast.success("Card reviewed!", {
          description: "Good job! Moving to next card."
        });
      }

      // Move to next card
      if (currentIndex < filteredFlashcards.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setFlipped(false);
      } else {
        toast.success("Session Complete!", {
          description: `You reviewed ${reviewedCount + 1} cards!`
        });
        setCurrentIndex(0);
        setFlipped(false);
      }
    } catch (error) {
      console.error("Failed to review card:", error);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setFlipped(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < filteredFlashcards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setFlipped(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading flashcards...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="flashcards-page" className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Flashcards</h1>
        <p className="text-muted-foreground">Spaced repetition for better retention</p>
      </div>

      {/* Subject Filter */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={selectedSubject === null ? "default" : "outline"}
          size="sm"
          className="rounded-full"
          onClick={() => {
            setSelectedSubject(null);
            setCurrentIndex(0);
            setFlipped(false);
          }}
        >
          All Subjects
        </Button>
        {subjects.map((subject) => (
          <Button
            key={subject.id}
            variant={selectedSubject === subject.id ? "default" : "outline"}
            size="sm"
            className="rounded-full border-border"
            onClick={() => {
              setSelectedSubject(subject.id);
              setCurrentIndex(0);
              setFlipped(false);
            }}
          >
            {subject.name}
          </Button>
        ))}
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Card {currentIndex + 1} of {filteredFlashcards.length}
        </p>
        <p className="text-sm text-muted-foreground">
          Reviewed: {reviewedCount}
        </p>
      </div>

      {/* Flashcard */}
      {currentCard ? (
        <div 
          className={`flashcard cursor-pointer ${flipped ? 'flipped' : ''}`}
          onClick={handleFlip}
          data-testid="flashcard"
        >
          <div className="flashcard-inner relative min-h-[300px]">
            {/* Front */}
            <div className="flashcard-front absolute inset-0 glass rounded-2xl p-8 border border-border flex flex-col">
              <div className="mb-4">
                <span className="text-xs px-3 py-1 rounded-full bg-primary/20 text-primary">
                  {subjects.find(s => s.id === currentCard.subject_id)?.name}
                </span>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <p className="text-lg md:text-xl text-center font-medium">{currentCard.front}</p>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-4">
                Click to reveal answer
              </p>
            </div>

            {/* Back */}
            <div className="flashcard-back absolute inset-0 glass rounded-2xl p-8 border border-primary/30 bg-primary/5 flex flex-col">
              <div className="mb-4 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-xs text-primary font-medium">Answer</span>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <p className="text-base md:text-lg text-center leading-relaxed" 
                   dangerouslySetInnerHTML={{ 
                     __html: currentCard.back
                       .replace(/\*\*(.*?)\*\*/g, '<strong class="text-primary">$1</strong>')
                       .replace(/\n\n/g, '<br/><br/>')
                       .replace(/\n/g, '<br/>') 
                   }} 
                />
              </div>
              <p className="text-xs text-muted-foreground text-center mt-4">
                How well did you know this?
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="glass rounded-2xl p-12 border border-border text-center">
          <p className="text-muted-foreground">No flashcards available for this subject.</p>
        </div>
      )}

      {/* Review Buttons (shown when flipped) */}
      {flipped && currentCard && (
        <div className="flex justify-center gap-3">
          <Button
            data-testid="review-hard-btn"
            variant="outline"
            className="rounded-full border-destructive/30 text-destructive hover:bg-destructive/10"
            onClick={() => handleReview(1)}
          >
            <X className="w-4 h-4 mr-2" />
            Didn't Know
          </Button>
          <Button
            data-testid="review-medium-btn"
            variant="outline"
            className="rounded-full border-warning/30 text-warning hover:bg-warning/10"
            onClick={() => handleReview(3)}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Somewhat Knew
          </Button>
          <Button
            data-testid="review-easy-btn"
            variant="outline"
            className="rounded-full border-success/30 text-success hover:bg-success/10"
            onClick={() => handleReview(5)}
          >
            <Check className="w-4 h-4 mr-2" />
            Knew It!
          </Button>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          className="rounded-full"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        <Button
          variant="outline"
          onClick={handleNext}
          disabled={currentIndex >= filteredFlashcards.length - 1}
          className="rounded-full"
        >
          Next
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default FlashcardsPage;
